import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, Button } from '../../components';
import { getCurrentUser } from '../../data/session';
import { logout, refreshSession, routeAfterAuth } from '../../services/auth-service';

const ROLE_LABEL: Record<string, string> = {
  partner:   'Partner',
  associate: 'Associate',
  investor:  'Investor',
  founder:   'Founder',
  mentor:    'Mentor',
  admin:     'Admin',
};

const HourglassIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6l4 2m5-2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const RejectIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const Pending: React.FC = () => {
  const navigate = useNavigate();
  const user = getCurrentUser();

  const [status, setStatus] = useState<string>('pending');
  const [rejectionReason, setRejectionReason] = useState<string>('');
  const [polling, setPolling] = useState(false);

  // If there's no session at all, kick to /login.
  useEffect(() => {
    if (!user.id) {
      navigate('/login');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll every 5s — redirect as soon as status becomes approved/active.
  useEffect(() => {
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const tick = async () => {
      if (!alive) return;
      setPolling(true);
      try {
        const res = await refreshSession();
        if (!alive) return;
        if (!res) {
          // Firebase not yet rehydrated — retry shortly.
          if (alive) timer = setTimeout(tick, 3_000);
          return;
        }
        const s = (res.user as any).status;
        const reason = (res.user as any).rejectionReason || '';
        setStatus(s);
        setRejectionReason(reason);
        const target = routeAfterAuth(res.user as any);
        if (target !== '/pending') {
          navigate(target);
          return;
        }
      } catch {
        // Stay on the page; user may be offline or token expired.
      } finally {
        if (alive) setPolling(false);
      }
      if (alive) timer = setTimeout(tick, 5_000);
    };

    tick();
    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
    };
  }, [navigate]);

  const handleSignOut = async () => {
    await logout();
    navigate('/login');
  };

  const handleCheckNow = async () => {
    setPolling(true);
    try {
      const res = await refreshSession();
      if (!res) return;
      setStatus((res.user as any).status);
      setRejectionReason((res.user as any).rejectionReason || '');
      const target = routeAfterAuth(res.user as any);
      if (target !== '/pending') navigate(target);
    } finally {
      setPolling(false);
    }
  };

  const roleName = ROLE_LABEL[user.role] || user.role;
  const isRejected = status === 'rejected';

  return (
    <div className="min-h-screen bg-bg flex items-center justify-center p-6">
      <div className="w-full max-w-xl">
        <div className="text-center mb-8">
          <img src="/logo.png" alt="BunyadKar" className="h-12 w-auto mx-auto" />
        </div>

        <Card>
          <div className="text-center py-2">
            <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 ${isRejected ? 'bg-danger/15' : 'bg-warning/15'}`}>
              {isRejected ? <RejectIcon className="w-10 h-10 text-danger" /> : <HourglassIcon className="w-10 h-10 text-warning" />}
            </div>

            {isRejected ? (
              <>
                <h1 className="font-display text-3xl md:text-4xl tracking-wide text-text uppercase mb-3">
                  Application not approved
                </h1>
                <p className="font-serif-display text-lg italic text-muted mb-3">
                  Your <span className="not-italic font-semibold text-text">{roleName.toLowerCase()}</span> application
                  was reviewed and not approved at this time.
                </p>
                {rejectionReason && (
                  <div className="text-sm text-text bg-surface border border-line rounded-card p-3 mt-2 mb-4 text-left">
                    <div className="text-xs font-medium text-muted uppercase tracking-wide mb-1">Reason</div>
                    {rejectionReason}
                  </div>
                )}
              </>
            ) : (
              <>
                <h1 className="font-display text-3xl md:text-4xl tracking-wide text-text uppercase mb-3">
                  Awaiting approval
                </h1>
                <p className="font-serif-display text-lg italic text-muted mb-2">
                  Your <span className="not-italic font-semibold text-text">{roleName.toLowerCase()}</span> account
                  is under review.
                </p>
                <p className="font-serif-display text-base italic text-muted mb-6">
                  We typically review new {roleName.toLowerCase()} accounts within 1–3 business days.
                  We'll email you the moment it's approved.
                </p>
              </>
            )}

            <div className="flex flex-col sm:flex-row gap-3 justify-center mt-4">
              {!isRejected && (
                <Button variant="primary" size="md" onClick={handleCheckNow} disabled={polling}>
                  {polling ? 'Checking…' : 'Check status now'}
                </Button>
              )}
              <Button variant="ghost" size="md" onClick={handleSignOut}>
                Sign out
              </Button>
            </div>

            <p className="text-xs text-muted mt-8">
              Wrong account?{' '}
              <Link to="/login" className="text-chart hover:text-chart/80 font-medium">
                Sign in with a different one
              </Link>
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Pending;
